import React, { useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RecommendationIcon from '@mui/icons-material/LocalOffer';
import BranchIcon from '@mui/icons-material/AccountTree';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface TreeNodePosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Option {
  text: string;
  nextQuestion?: string;
  recommendations?: string[];
}

interface DraggableTreeNodeProps {
  id: string;
  question: string;
  options: Option[];
  position: TreeNodePosition;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDrag: (id: string, deltaX: number, deltaY: number) => void;
  onClick: () => void;
  isLinking?: boolean;
  isLinkStart?: boolean;
  isReference?: boolean;
  scale?: number;
}

const DraggableTreeNode: React.FC<DraggableTreeNodeProps> = ({
  id,
  question,
  options,
  position,
  onEdit,
  onDelete,
  onDuplicate,
  onDrag,
  onClick,
  isLinking,
  isLinkStart,
  isReference,
  scale = 1
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const nodeRef = useRef<SVGGElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isLinking) {
      onClick();
      return;
    }

    // Prevent event from bubbling up to parent elements
    e.stopPropagation();
    e.preventDefault();
    
    setIsDragging(true);
    setStartX(e.clientX);
    setStartY(e.clientY);

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      e.stopPropagation();
      e.preventDefault();

      const deltaX = (e.clientX - startX);
      const deltaY = (e.clientY - startY);

      onDrag(id, deltaX, deltaY);
      setStartX(e.clientX);
      setStartY(e.clientY);
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isLinking) {
      e.stopPropagation();
      onClick();
    }
  };

  return (
    <g
      ref={nodeRef}
      transform={`translate(${position.x}, ${position.y})`}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      pointerEvents="all"
    >
      <rect
        width={position.width}
        height={position.height}
        rx={8}
        fill={isLinkStart ? '#4CAF50' : isReference ? '#f5f5f5' : 'white'}
        stroke={isLinkStart ? '#2E7D32' : isReference ? '#9e9e9e' : '#2196F3'}
        strokeWidth={2}
      />
      <text
        x={position.width / 2}
        y={30}
        textAnchor="middle"
        fill={isLinkStart ? 'white' : isReference ? '#757575' : '#000'}
        style={{
          fontSize: '14px',
          fontWeight: isReference ? 'normal' : 'bold',
          fontStyle: isReference ? 'italic' : 'normal',
          userSelect: 'none',
          pointerEvents: 'none'
        }}
      >
        {question}
      </text>
      <text
        x={position.width / 2}
        y={50}
        textAnchor="middle"
        fill={isLinkStart ? '#E8F5E9' : isReference ? '#9e9e9e' : '#666'}
        style={{ 
          fontSize: '12px',
          userSelect: 'none',
          pointerEvents: 'none'
        }}
      >
        {`${options.length} option${options.length !== 1 ? 's' : ''}`}
      </text>
      {!isReference && (
        <>
          <g
            transform={`translate(${position.width - 30}, 20)`}
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <circle r="12" fill="#2196F3" />
            <text
              textAnchor="middle"
              fill="white"
              style={{ fontSize: '16px' }}
              y="5"
            >
              ✎
            </text>
          </g>
          <g
            transform={`translate(${position.width - 60}, 20)`}
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <circle r="12" fill="#2196F3" />
            <text
              textAnchor="middle"
              fill="white"
              style={{ fontSize: '16px' }}
              y="5"
            >
              ⎘
            </text>
          </g>
        </>
      )}
    </g>
  );
};

export default DraggableTreeNode; 